import { useApolloClient } from "@apollo/client";
import { assert } from "@msys/common";
import {
  ButtonInputWithPopover,
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  GridGroupingColDefOverride,
  GridRowId,
  ListHeader,
  ModalOpenButton,
  type PopoverOpenButtonRef,
  Switch,
  useDataGridGroupExpandedStore,
} from "@msys/ui";
import {
  ErrorOutline as ErrorOutlineIcon,
  Functions as FunctionsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridCallbackDetails,
  gridClasses,
  GridRowParams,
  GridRowSelectionModel,
  GridRowSelectionPropagation,
  GridTreeDataGroupingCell,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import { search, sortKind } from "fast-fuzzy";
import { Form, Formik, useFormikContext } from "formik";
import {
  capitalize,
  cloneDeep,
  difference,
  get,
  isEqual,
  pick,
  uniqueId,
} from "lodash-es";
import { useSnackbar } from "notistack";
import omitDeep from "omit-deep-lodash";
import React, { Dispatch, SetStateAction } from "react";
import { useLatest } from "react-use";
import * as Yup from "yup";
import {
  DocType,
  namedOperations,
  PermissionName,
  Props2,
} from "../../../clients/graphqlTypes.js";
import type { DocumentByType } from "../../auth/RestrictedByDocumentPermissionByType.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  EditMode,
  SwitchEditModeButton,
} from "../../commons/button/SwitchEditModeButton.js";
import { FilterChipGroup } from "../../commons/filters/FilterChip.js";
import { ManualSave } from "../../commons/form-fields/ManualSave.js";
import { useStateWithUrlParams } from "../../commons/hooks/useStateWithUrlParams.js";
import { Page } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { highlightWithMatches } from "../../utils.js";
import { AllowedValuesFormWithOverride } from "../doc-items/AllowedValuesFormWithOverride.js";
import { ButtonSelectItemsMultiple } from "../doc-items/ButtonSelectItemsMultiple.js";
import { ItemSelect_ItemFragment } from "../doc-items/ItemSelect.generated.js";
import { formatItem } from "../doc-items/ItemSelect.js";
import {
  createShouldBeInAllowedValuesTest,
  defaultFormValues,
  FormValues as ItemPropsCustomizeOverrideModalFormValues,
  getEntryValue,
  hasPropValuesOverride,
  ItemPropsCustomizeOverrideInnerModal,
  overrideToFormValues,
  overrideValuesToInput,
} from "../doc-items/modals/ItemPropsCustomizeOverrideModal.js";
import { QuestionControlFormWithOverride } from "../doc-items/modals/QuestionControlFormWithOverride.js";
import { QuestionControlModal } from "../doc-items/modals/QuestionControlModal.js";
import {
  applyPropertyOverrides,
  getPropDisplayedValue,
  getPropertyDisplayedUnit,
  getPropertyType,
  getPropValues,
  groupProperties,
  isComputedProp,
  isNonComputedProp,
  isNumberType,
  PropType,
  PropValues,
  TypeFields,
  ungroupProperties,
} from "../doc-items/properties.js";
import { PropertyField } from "../doc-items/PropertyField.js";
import { defaultQuestionControlValues } from "../doc-items/QuestionControlSection.js";
import { usePropertyHelpers } from "../doc-items/usePropertyHelpers.js";
import { usePropertyUnits } from "../doc-items/usePropertyUnits.js";
import {
  getQuestionControlLabel,
  useQuestionContol,
} from "../doc-items/useQuestionContol.js";
import { useDataGridStateStore } from "../users/useDataGridStateStore.js";
import { hasAllowedValuesToOverride, renderAllowedValues } from "./helpers.js";
import {
  ManagePropertiesOverridesTable_ItemFragment,
  useDefineItemPropsOverridesBulkMutation,
} from "./ManagePropertiesOverridesTable.generated.js";
import { ManagePropertiesTable_ItemFragment } from "./ManagePropertiesTable.generated";

interface Props {
  docType: DocType;
  documentViewerPermissions: PermissionName[];
  items: ManagePropertiesOverridesTable_ItemFragment[];
  docId: string;
  versionNumber: number;
  refetchQueries?: string[];
}

declare const __propKey: unique symbol;
type PropKey = string & { readonly [__propKey]: true };

type PropertyFormValue = ItemPropsCustomizeOverrideModalFormValues &
  PropValues & {
    isComputed: boolean;
    key: string;
    type: PropType;
    item: Omit<ManagePropertiesOverridesTable_ItemFragment, "props2">;
  };

type PropertyGridValue = PropValues &
  TypeFields & {
    key: string;
    item: { id: string };
    prop: Props2;
    isComputed: boolean;
    match?: { index: number; length: number }[];
  };

type FormValues = Record<string, PropertyFormValue>;

export const getKey = (prop: {
  key: string;
  item: { id: string };
}): PropKey => {
  return `item:${prop.item.id}/prop:${prop.key}` as PropKey;
};

type Filters = {
  itemIds: string[];
  searchTerm: string;
  customizable: boolean;
};

const defaultFilters: Filters = {
  itemIds: [],
  searchTerm: "",
  customizable: true,
};

type Props2WithItem = Props2 & {
  item: Omit<ManagePropertiesOverridesTable_ItemFragment, "props2">;
};

const cleanFormValues = (values: PropertyFormValue) => {
  // step 1: clean attachments
  let _values: any = {
    ...values,
    allowedValuesMedia: values.allowedValuesMedia.map(avm => ({
      ...avm,
      media: avm.media
        ? pick(avm.media, "url", "title", "mimeType")
        : avm.media,
    })),
  };

  // step 2: pick only specific fields
  const fieldsToCompare = [
    "questionControl",
    "defaultValue",
    "clientVisibility",
    "allowedValuesExclude",
    "allowedValuesMedia",
  ];
  _values = pick(_values, ...fieldsToCompare);

  // step 3: clean graphql __typename
  _values = omitDeep(_values, ["__typename"]);

  return _values;
};

const isEqualFormValues = (
  values1: PropertyFormValue,
  values2: PropertyFormValue
) => {
  return isEqual(cleanFormValues(values1), cleanFormValues(values2));
};

function propWithItemToPropValue(prop: Props2WithItem): PropertyFormValue {
  const propertyType = getPropertyType(prop);
  const override =
    prop.item.itemPropsOverrides.find(po => po.key === prop.key) ?? null;
  return {
    ...getPropValues(prop),
    key: prop.key,
    type: propertyType,
    isComputed: isComputedProp(prop),
    item: prop.item,
    ...overrideToFormValues(override, propertyType),
  } satisfies PropertyFormValue;
}

export const ManagePropertiesOverridesTable = ({
  docType,
  documentViewerPermissions,
  items,
  docId,
  versionNumber,
  refetchQueries,
  pageProps,
}: Props & { pageProps: React.ComponentProps<typeof Page> }) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const { enqueueSnackbar } = useSnackbar();
  const { getUnitLabel } = usePropertyUnits();
  const { getBoolLabel } = usePropertyHelpers();
  const viewer = useUserData().currentUser!;

  const gridApiRef = useGridApiRef();

  const [editMode, setEditMode] = React.useState<EditMode>("normal");

  const [selectedProperties, setSelectedProperties] =
    React.useState<GridRowSelectionModel>(defaultSelectionModel);

  const latestSelectedProperties = useLatest(selectedProperties);

  const isRowSelectable = React.useCallback(
    (params: GridRowParams<PropertyGridValue>) => {
      const node = gridApiRef.current?.getRowNode(params.id);
      if (node?.type === "group") {
        return (node?.children ?? []).some(childId => {
          const childRow =
            gridApiRef.current?.getRow<PropertyGridValue>(childId);
          return (
            childRow &&
            isNonComputedProp(childRow.prop) &&
            childRow.prop.customizable
          );
        });
      }
      if (node?.type === "leaf") {
        return (
          isNonComputedProp(params.row.prop) && params.row.prop.customizable
        );
      }
      return false;
    },
    [gridApiRef]
  );

  const document: DocumentByType = React.useMemo(
    () => ({
      docType,
      viewerPermissions: documentViewerPermissions,
    }),
    [docType, documentViewerPermissions]
  );

  const [openPropertyKey, setOpenPropertyKey] = React.useState<PropKey | null>(
    null
  );

  const theme = useTheme();

  const [filters, setFilters] = useStateWithUrlParams<Filters>(
    "filters",
    defaultFilters
  );
  const isFilterActive = Boolean(
    filters.searchTerm || filters.itemIds.length > 0 || filters.customizable
  );
  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const { isGroupExpandedByDefault, onStateChange } =
    useDataGridGroupExpandedStore(gridApiRef, `${docType}-${docId}`, {
      keepNonExistingKeys: isFilterActive,
    });

  const propsWithItems = React.useMemo(
    (): Props2WithItem[] =>
      items.flatMap(({ props2, ...item }) =>
        // sort according to groups
        ungroupProperties(groupProperties(props2)).map(property => ({
          ...property,
          item,
        }))
      ),
    [items]
  );

  const stateStore = useDataGridStateStore("ManagePropertiesOverridesTable", {
    rowGrouping: { model: ["item"] },
  });

  const initialValues = React.useMemo(
    (): FormValues =>
      Object.fromEntries(
        propsWithItems.map(prop => [
          getKey(prop),
          propWithItemToPropValue(prop),
        ])
      ),
    [propsWithItems]
  );

  const filteredRows = React.useMemo((): PropertyGridValue[] => {
    let rows: PropertyGridValue[] = propsWithItems.map((prop, index) => {
      return {
        ...getPropValues(prop),
        key: prop.key,
        type: getPropertyType(prop),
        isComputed: isComputedProp(prop),
        item: { id: prop.item.id },
        prop,
      };
    });

    if (filters.customizable) {
      rows = rows.filter(r => isNonComputedProp(r.prop) && r.prop.customizable);
    }

    if (filters.itemIds && filters.itemIds.length > 0) {
      rows = rows.filter(r => filters.itemIds.includes(r.item.id));
    }

    if (filters.searchTerm) {
      const searchResultRows = search(filters.searchTerm, rows, {
        keySelector: row => row.key,
        threshold: 0.7,
        returnMatchData: true,
        sortBy: sortKind.insertOrder,
      });

      rows = searchResultRows.map(r => ({
        ...r.item,
        match: r.match ? [r.match] : undefined,
      }));
    }

    return rows;
  }, [
    filters.customizable,
    filters.itemIds,
    filters.searchTerm,
    propsWithItems,
  ]);

  const onRowSelectionModelChange = React.useCallback(
    (
      rowSelectionModel: GridRowSelectionModel,
      details: GridCallbackDetails<any>
    ) => {
      if (rowSelectionModel.type === "exclude") {
        setSelectedProperties({
          type: "include",
          ids: new Set(
            filteredRows
              .map(getRowId)
              .filter(id => !rowSelectionModel.ids.has(id))
          ),
        });
        return;
      }

      // expand selected group if it wasn't expanded before
      difference(
        Array.from(rowSelectionModel.ids),
        Array.from(latestSelectedProperties.current.ids)
      ).forEach(id => {
        const row = gridApiRef.current?.getRowNode?.(id);
        if (
          row &&
          row.type === "group" &&
          row.isAutoGenerated &&
          row.groupingKey &&
          !row.childrenExpanded
        ) {
          gridApiRef.current?.setRowChildrenExpansion?.(id, true);
        }
      });

      setSelectedProperties(rowSelectionModel);
    },
    [gridApiRef, latestSelectedProperties, filteredRows]
  );

  const [previousPropKey, nextPropKey] = React.useMemo(() => {
    const currentKeyIndex = openPropertyKey
      ? filteredRows.findIndex(p => getKey(p) === openPropertyKey)
      : null;

    const previousKey =
      currentKeyIndex !== null && filteredRows[currentKeyIndex - 1]
        ? getKey(filteredRows[currentKeyIndex - 1])
        : null;
    const nextKey =
      currentKeyIndex !== null && filteredRows[currentKeyIndex + 1]
        ? getKey(filteredRows[currentKeyIndex + 1])
        : null;

    return [previousKey, nextKey];
  }, [filteredRows, openPropertyKey]);

  const columns = React.useMemo(
    (): GridColDef<PropertyGridValue>[] => [
      {
        field: "key",
        headerName: t("Key", { ns: "Global" }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return highlightWithMatches(property.key, property.match);
        },
      },
      {
        field: "label",
        headerName: t("Label", { ns: "Global" }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return highlightWithMatches(property.prop.label, property.match);
        },
      },
      {
        field: "type",
        headerName: t("Type", {
          ns: "QuoteItem",
        }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return capitalize(property.type);
        },
      },
      {
        field: "allowMultiple",
        headerName: t("Multiple", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 0.5,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return property.isArray === true || property.isArray === false
            ? getBoolLabel(property.isArray)
            : null;
        },
      },
      {
        field: "unit",
        headerName: t("Unit", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return property.isNumber &&
            isNumberType(property.prop) &&
            property.prop.unit
            ? getUnitLabel(property.prop.unit)
            : null;
        },
      },
      ...(docType === "TEMPLATE" &&
      viewer.organisation.capabilities.includes("TEMPLATE_SHARING")
        ? ([
            {
              field: "customizable",
              headerName: t("Customisable", { ns: "QuoteItem" }),
              sortable: false,
              groupable: false,
              flex: 1,
              renderCell({ row: property, rowNode }) {
                if (rowNode.type === "group") return null;
                return isNonComputedProp(property.prop)
                  ? getBoolLabel(property.prop.customizable)
                  : null;
              },
            },
          ] as GridColDef<PropertyGridValue>[])
        : []),
      {
        field: "allowedValues",
        headerName: t("Allowed values", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 2,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return <AllowedValuesCell values={property} />;
        },
      },
      {
        field: "prompt",
        headerName: t("Prompt", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 2,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return isNonComputedProp(property.prop)
            ? property.prop.prompt || "–"
            : null;
        },
      },
      {
        field: "questionControl",
        headerName: t("Question control", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 2,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return (
            <QuestionControlCell
              values={property}
              disabled={editMode === "bulk"}
            />
          );
        },
      },
      {
        field: "group",
        headerName: t("Group", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        // hideable: true,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return property.prop.group;
        },
      },
      {
        field: "askIfExpr",
        headerName: t("Ask if", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 0,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return isNonComputedProp(property.prop) ? (
            property.prop.askIfExpr ? (
              <FunctionsIcon fontSize="small" color="secondary" />
            ) : null
          ) : null;
        },
      },
      {
        field: "value",
        headerName: t("Value", { ns: "Global" }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return (
            <ValueCell
              values={property}
              docId={docId}
              disabled={editMode === "bulk"}
            />
          );
        },
      },
      {
        field: "visibility",
        headerName: t("Visibility", { ns: "Global" }),
        sortable: false,
        groupable: false,
        flex: 0,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return (
            <VisibilityCell values={property} disabled={editMode === "bulk"} />
          );
        },
      },
      {
        field: "configure",
        headerName: "",
        sortable: false,
        groupable: false,
        flex: 0,
        minWidth: 80,
        maxWidth: 80,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return isNonComputedProp(property.prop) &&
            property.prop.customizable ? (
            <>
              <ConfigureIconButton
                values={property}
                setOpenPropertyKey={setOpenPropertyKey}
                disabled={editMode === "bulk"}
              />
              <ResetIconButton
                values={property}
                disabled={editMode === "bulk"}
              />
            </>
          ) : null;
        },
      },
    ],
    [
      t,
      docType,
      docId,
      viewer.organisation.capabilities,
      getBoolLabel,
      getUnitLabel,
      editMode,
    ]
  );

  const itemsByIds = React.useMemo(
    (): Record<string, ManagePropertiesTable_ItemFragment> =>
      items.reduce((acc, item) => ({ ...acc, [item.id]: item }), {}),
    [items]
  );

  const groupingColDef =
    React.useMemo((): GridGroupingColDefOverride<PropertyGridValue> => {
      return {
        headerName: t("Item", { ns: "Global" }),
        flex: 2,
        minWidth: 120,
        renderCell: params => {
          if (params.rowNode.type === "group") {
            const item = itemsByIds[params.value];
            assert(item, `No item for group: ${params.value}`);
            return (
              <GridTreeDataGroupingCell
                {...params}
                hideDescendantCount
                rowNode={{
                  ...params.rowNode,
                  groupingKey: params.rowNode.groupingKey,
                }}
                formattedValue={formatItem(item)}
              />
            );
          }
          return null;
        },
      } as GridGroupingColDefOverride<PropertyGridValue>;
    }, [itemsByIds, t]);

  const formId = React.useMemo(() => uniqueId(), []);

  const client = useApolloClient();
  const [defineItemPropsOverridesBulk] =
    useDefineItemPropsOverridesBulkMutation({
      client,
      refetchQueries: [
        namedOperations.Query.ItemPropsOverrideModal,
        ...(refetchQueries ?? []),
      ],
    });

  const handleSubmit = async (values: FormValues) => {
    try {
      await defineItemPropsOverridesBulk({
        variables: {
          input: {
            templateId: docId,
            versionNumber,
            items: Object.entries(
              Object.entries(values).reduce<
                Record<string, PropertyFormValue[]>
              >((acc, [key, values]) => {
                const itemId = values.item.id;
                if (!acc[itemId]) acc[itemId] = [];
                acc[itemId].push(values);
                return acc;
              }, {})
            ).map(([itemId, entries]) => ({
              itemId,
              overrides: entries
                .filter(values => hasPropValuesOverride(values))
                .map(values => overrideValuesToInput(values.key, values)),
            })),
          },
        },
      });
      enqueueSnackbar(t("Overrides applied", { ns: "QuoteItem" }));
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const localeText = React.useMemo(
    () => ({
      footerTotalRows: "",
      footerTotalVisibleRows: (visibleCount: number, totalCount: number) =>
        `${t("Items", { ns: "QuoteItem" })}: ${visibleCount}, ${t("Properties", { ns: "QuoteItem" })}: ${totalCount}`,
    }),
    [t]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape(
        Object.fromEntries(
          propsWithItems
            .filter(p => isNonComputedProp(p) && p.customizable)
            .map(prop => [
              getKey(prop),
              Yup.object().shape({
                defaultValue: Yup.mixed()
                  .nullable()
                  .optional()
                  .test(
                    "shouldBeInAllowedValues",
                    t("Not in allowed values", { ns: "QuoteItem" }),
                    createShouldBeInAllowedValuesTest(prop)
                  ),
              }),
            ])
        )
      ),
    [propsWithItems, t]
  );

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnMount={false}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, dirty, handleReset, setFieldValue }) => (
        <Page
          {...pageProps}
          action={
            <>
              {editMode === "bulk" && (
                <>
                  <BulkEditAction
                    selectedProperties={selectedProperties}
                    docType={docType}
                    disabled={selectedProperties.ids.size === 0}
                    propsWithItems={propsWithItems}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              )}
              <ManualSave
                onCancel={handleReset}
                disabled={isSubmitting}
                disabledCancel={!dirty}
                form={formId}
                stackProps={{
                  maxWidth: `${theme.layout.formSubmitWidth.md}px`,
                }}
                buttonSize="medium"
              />
            </>
          }
        >
          <PageContainer>
            <Form
              id={formId}
              style={{
                display: "flex",
                flex: 1,
                overflow: "auto",
              }}
            >
              <Stack
                direction="column"
                spacing={2}
                sx={theme => ({
                  [`.${gridClasses.cell}.deleted`]: {
                    backgroundColor: theme.palette.grey["200"],
                    textDecoration: "line-through",
                  },
                })}
                width="100%"
              >
                <ListHeader
                  ActionButtons={
                    <SwitchEditModeButton
                      editMode={editMode}
                      onEditModeChange={setEditMode}
                    />
                  }
                  SearchField={
                    <DebouncedSearchInput
                      defaultValue={filters.searchTerm}
                      placeholder={t("Search", { ns: "Global" })}
                      onChangeSearchTerm={newSearchTerm => {
                        setFilters(state => ({
                          ...state,
                          searchTerm: newSearchTerm,
                        }));
                      }}
                      autoFocus
                      style={{
                        flexGrow: 1,
                        maxWidth: `${theme.layout.searchFieldWidth.xs}px`,
                      }}
                    />
                  }
                  QuickFilter={
                    <Stack
                      direction="row"
                      spacing={2}
                      flexWrap="wrap"
                      alignItems="center"
                      minHeight="32px"
                      useFlexGap
                    >
                      <ButtonSelectItemsMultiple
                        items={items}
                        itemIds={filters.itemIds}
                        onChange={itemIds =>
                          setFilters(f => ({ ...f, itemIds }))
                        }
                      />
                      <Switch
                        label={t("Customisable", { ns: "QuoteItem" })}
                        checked={filters.customizable}
                        onChange={(_, checked) => {
                          setFilters(f => ({ ...f, customizable: checked }));
                        }}
                      />
                    </Stack>
                  }
                  FilterChips={
                    canShowChips(filters) ? (
                      <FilterChips
                        items={items}
                        filters={filters}
                        setFilters={setFilters}
                        onReset={resetFilters}
                      />
                    ) : undefined
                  }
                />
                <DataGrid<PropertyGridValue>
                  apiRef={gridApiRef}
                  stateStore={stateStore}
                  columns={columns}
                  rows={filteredRows}
                  rowCount={filteredRows.length}
                  getRowId={getRowId}
                  paginationMode="server"
                  hideFooterPagination={true}
                  sortingMode="client"
                  autoHeight={false}
                  disableVirtualization={false}
                  virtualizeColumnsWithAutoRowHeight={true}
                  // selection
                  checkboxSelection={editMode === "bulk"}
                  disableRowSelectionOnClick
                  rowSelectionModel={
                    editMode === "bulk"
                      ? selectedProperties
                      : defaultSelectionModel
                  }
                  onRowSelectionModelChange={onRowSelectionModelChange}
                  isRowSelectable={isRowSelectable}
                  // tree data
                  treeData
                  getTreeDataPath={getTreeDataPath}
                  groupingColDef={groupingColDef}
                  rowSelectionPropagation={rowSelectionPropagation}
                  // Virtualization optimizations
                  pagination={false}
                  getRowHeight={getRowHeight}
                  rowBufferPx={150}
                  columnBufferPx={40}
                  // group expansion
                  isGroupExpandedByDefault={isGroupExpandedByDefault}
                  onStateChange={onStateChange}
                  localeText={localeText}
                />
              </Stack>
            </Form>
          </PageContainer>
          {openPropertyKey &&
            (() => {
              const row = filteredRows.find(p => getKey(p) === openPropertyKey);
              if (!row) return null;
              const property = values[openPropertyKey];
              if (!property) return null;
              const item = items.find(i => i.id === row.item.id);
              if (!item) return null;
              return (
                <ItemPropsCustomizeOverrideInnerModal
                  key={openPropertyKey}
                  templateId={docId}
                  itemId={row.item.id}
                  item={item}
                  versionNumber={versionNumber}
                  propertyKey={row.key}
                  override={null}
                  initialValues={cloneDeep(property)}
                  handleSave={async (newOverrides, newValues) => {
                    await setFieldValue(`[${openPropertyKey}]`, {
                      ...property,
                      ...newValues,
                    });
                  }}
                  handlePrevious={
                    previousPropKey
                      ? () => {
                          setOpenPropertyKey(previousPropKey);
                        }
                      : null
                  }
                  handleNext={
                    nextPropKey
                      ? () => {
                          setOpenPropertyKey(nextPropKey);
                        }
                      : null
                  }
                  handleClose={() => setOpenPropertyKey(null)}
                />
              );
            })()}
        </Page>
      )}
    </Formik>
  );
};

const defaultSelectionModel: GridRowSelectionModel = {
  type: "include",
  ids: new Set(),
};

const getRowId = (property: PropertyGridValue): GridRowId => getKey(property);
const getTreeDataPath = (property: PropertyGridValue): string[] => [
  property.item.id,
  property.key,
];
const isGroupExpandedByDefault = () => false;
const getRowHeight = () => 36;

const rowSelectionPropagation: GridRowSelectionPropagation = {
  descendants: true,
  parents: true,
};

function canShowChips(filters: Filters): boolean {
  return Boolean(filters.itemIds.length);
}

function FilterChips({
  filters,
  setFilters,
  onReset,
  items,
}: {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  onReset?(): void | Promise<void>;
  items: ItemSelect_ItemFragment[];
}) {
  const { t } = useTranslate(["QuoteItem", "Global"]);

  return canShowChips(filters) ? (
    <Stack
      direction={"row"}
      spacing={1}
      flexWrap="wrap"
      alignItems="center"
      useFlexGap
    >
      <FilterChipGroup
        label={""}
        values={filters.itemIds}
        getValueLabel={value => {
          const item = items.find(i => i.id === value);
          if (!item) return "";
          return formatItem(item);
        }}
        setValues={value =>
          setFilters(state => ({
            ...state,
            itemIds: value ?? [],
          }))
        }
      />
      {onReset && (
        <Typography variant="body2">
          <Link onClick={onReset} color="secondary" sx={{ cursor: "pointer" }}>
            {t("Clear all", { ns: "Global" })}
          </Link>
        </Typography>
      )}
    </Stack>
  ) : null;
}

function ConfigureIconButton({
  values,
  setOpenPropertyKey,
  disabled,
}: {
  values: PropertyGridValue;
  setOpenPropertyKey: (key: PropKey) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const formikContext = useFormikContext<FormValues>();
  const propertyKey = getKey(values);
  const property = formikContext.values[propertyKey];
  assert(property, `No property found for key: ${propertyKey}`);

  const hasOverride = hasPropValuesOverride(property);

  return (
    <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
      <IconButton
        color="primary"
        size={"small"}
        onClick={() => {
          setOpenPropertyKey(propertyKey);
        }}
        disabled={disabled}
      >
        <Badge color="secondary" variant="dot" invisible={!hasOverride}>
          <Tooltip title={t("Customise property", { ns: "QuoteItem" })}>
            <SettingsIcon />
          </Tooltip>
        </Badge>
      </IconButton>
    </RestrictedByOrganisationPermissionWithDebug>
  );
}

function ResetIconButton({
  values,
  disabled,
}: {
  values: PropertyGridValue;
  disabled?: boolean;
}) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const formikContext = useFormikContext<FormValues>();
  const propertyKey = getKey(values);
  const property = formikContext.values[propertyKey];
  assert(property, `No property found for key: ${propertyKey}`);

  const hasOverride = hasPropValuesOverride(property);

  return (
    <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
      <ModalOpenButton
        Modal={ConfirmModal}
        modalProps={{
          title: t("Are you sure you want to reset overrides for property?", {
            ns: "QuoteItem",
          }),
          handleConfirm: async () => {
            await formikContext.setValues({
              ...formikContext.values,
              [propertyKey]: { ...property, ...defaultFormValues },
            });
            await formikContext.setFieldTouched(
              `[${propertyKey}].defaultValue`,
              true
            );
          },
        }}
      >
        <IconButton
          color="primary"
          size={"small"}
          disabled={disabled || !hasOverride}
        >
          <Tooltip title={t("Reset", { ns: "Global" })}>
            <RestartAltIcon />
          </Tooltip>
        </IconButton>
      </ModalOpenButton>
    </RestrictedByOrganisationPermissionWithDebug>
  );
}

function AllowedValuesCell({
  values,
  disabled,
}: {
  values: PropertyGridValue;
  disabled?: boolean;
}) {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const { getBoolLabel, getNumberLabel } = usePropertyHelpers();

  const formikContext = useFormikContext<FormValues>();
  const propertyKey = getKey(values);
  const property = formikContext.values[propertyKey];

  const hasAllowedValuesOverride =
    (property?.allowedValuesExclude &&
      property?.allowedValuesExclude.length > 0) ||
    (property?.allowedValuesMedia && property?.allowedValuesMedia.length > 0);

  const isCustomizable =
    isNonComputedProp(values.prop) &&
    values.prop.customizable &&
    hasAllowedValuesToOverride(values.prop);

  const displayValue = isNonComputedProp(values.prop)
    ? renderAllowedValues(
        values.prop,
        t,
        getNumberLabel,
        getBoolLabel,
        property?.allowedValuesExclude,
        property?.allowedValuesMedia
      ) || "–"
    : "";

  const popoverOpenButtonRef = React.useRef<PopoverOpenButtonRef>();

  if (isCustomizable) {
    return (
      <Badge
        color="secondary"
        variant="dot"
        invisible={!hasAllowedValuesOverride}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ ".MuiBadge-badge": { top: 2 } }}
      >
        <ButtonInputWithPopover
          label={displayValue}
          popoverRef={popoverOpenButtonRef}
          disabled={disabled}
          overflowVisible={true}
          popoverWidth="xxl"
          content={
            <Box p={1}>
              <AllowedValuesFormWithOverride
                key={propertyKey}
                property={values.prop}
                initialValues={cloneDeep(property)}
                handleSubmit={async newValues => {
                  await formikContext.setFieldValue(`[${propertyKey}]`, {
                    ...property,
                    ...newValues,
                  });
                  await formikContext.setFieldTouched(
                    `[${propertyKey}].defaultValue`,
                    true
                  );
                  popoverOpenButtonRef.current?.handleClose();
                }}
                handleCancel={() => {
                  popoverOpenButtonRef.current?.handleClose();
                }}
              />
            </Box>
          }
        />
      </Badge>
    );
  }

  return (
    <Badge
      color="secondary"
      variant="dot"
      invisible={!hasAllowedValuesOverride}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={{ ".MuiBadge-badge": { top: 4 } }}
    >
      {displayValue}
    </Badge>
  );
}

function QuestionControlCell({
  values,
  disabled,
}: {
  values: PropertyGridValue;
  disabled: boolean;
}) {
  const { askWhomLabels, askWhenLabels } = useQuestionContol();

  const formikContext = useFormikContext<FormValues>();
  const propertyKey = getKey(values);
  const property = formikContext.values[propertyKey];

  const hasQuestionControlOverride =
    property.questionControl !== undefined && property.questionControl !== null;

  const isCustomizable =
    isNonComputedProp(values.prop) && values.prop.customizable;

  const displayValue = isNonComputedProp(values.prop)
    ? getQuestionControlLabel(
        property.questionControl ?? values.prop.questionControl,
        { askWhenLabels, askWhomLabels }
      ) || "–"
    : "";

  const popoverOpenButtonRef = React.useRef<PopoverOpenButtonRef>();

  if (isCustomizable && isNonComputedProp(values.prop)) {
    return (
      <Badge
        color="secondary"
        variant="dot"
        invisible={!hasQuestionControlOverride}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ ".MuiBadge-badge": { top: 2 } }}
      >
        <ButtonInputWithPopover
          label={displayValue}
          popoverRef={popoverOpenButtonRef}
          disabled={disabled}
          overflowVisible={true}
          popoverWidth="xxl"
          content={
            <Box p={1}>
              <QuestionControlFormWithOverride
                key={propertyKey}
                hidePrompt={true}
                questionControlOverride={property.questionControl}
                questionControlBase={values.prop.questionControl}
                handleSubmit={async (values, overrideExists) => {
                  await formikContext.setFieldValue(`[${propertyKey}]`, {
                    ...property,
                    questionControl: overrideExists
                      ? values.questionControl
                      : null,
                  });
                  popoverOpenButtonRef.current?.handleClose();
                }}
                handleCancel={() => {
                  popoverOpenButtonRef.current?.handleClose();
                }}
              />
            </Box>
          }
        />
      </Badge>
    );
  }

  return (
    <Badge
      color="secondary"
      variant="dot"
      invisible={!hasQuestionControlOverride}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={{ ".MuiBadge-badge": { top: 4 } }}
    >
      <Typography variant="body2">{displayValue}</Typography>
    </Badge>
  );
}

function VisibilityCell({
  values,
  disabled,
}: {
  values: PropertyGridValue;
  disabled?: boolean;
}) {
  const { getBoolLabel } = usePropertyHelpers();
  const { t } = useTranslate(["Global"]);

  const { values: formikValues, setFieldValue } =
    useFormikContext<FormValues>();
  const propertyKey = getKey(values);
  const property = formikValues[propertyKey];

  const hasVisibilityOverride =
    property.clientVisibility !== undefined &&
    property.clientVisibility !== null;

  const isCustomizable =
    isNonComputedProp(values.prop) && values.prop.customizable;

  if (isCustomizable) {
    return (
      <Badge
        color="secondary"
        variant="dot"
        invisible={!hasVisibilityOverride}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ ".MuiBadge-badge": { top: 8 } }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" width="100%">
          <Checkbox
            checked={
              property.clientVisibility !== null &&
              property.clientVisibility !== undefined
                ? property.clientVisibility
                : values.prop.clientVisibility
            }
            onChange={async (_, checked) => {
              await setFieldValue(`[${propertyKey}]`, {
                ...property,
                clientVisibility: checked,
              });
            }}
            disabled={disabled}
            size="small"
          />
          <IconButton
            color="primary"
            size="small"
            onClick={async () => {
              await setFieldValue(`[${propertyKey}]`, {
                ...property,
                clientVisibility: null,
              });
            }}
            disabled={disabled || !hasVisibilityOverride}
          >
            <Tooltip title={t("Reset", { ns: "Global" })}>
              <RestartAltIcon />
            </Tooltip>
          </IconButton>
        </Stack>
      </Badge>
    );
  }

  return (
    <Badge
      color="secondary"
      variant="dot"
      invisible={!hasVisibilityOverride}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={{ ".MuiBadge-badge": { top: 4 } }}
    >
      {getBoolLabel(property?.clientVisibility ?? values.prop.clientVisibility)}
    </Badge>
  );
}

function ValueCell({
  values,
  docId,
  disabled,
}: {
  values: PropertyGridValue;
  docId: string;
  disabled?: boolean;
}) {
  const { getUnitLabel } = usePropertyUnits();
  const { getBoolLabel, getNumberLabel } = usePropertyHelpers();
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const {
    values: formikValues,
    errors,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<FormValues>();
  const propertyKey = getKey(values);
  const name = `[${propertyKey}].defaultValue`;
  const property = formikValues[propertyKey];
  const fieldError = get(errors, name) as string | undefined;

  const hasValueOverride = property.defaultValue !== undefined;

  const isCustomizable =
    isNonComputedProp(values.prop) && values.prop.customizable;

  if (isCustomizable) {
    const newProperty = applyPropertyOverrides(
      values.prop,
      property.defaultValue,
      property.allowedValuesExclude
    );
    return (
      <Badge
        color="secondary"
        variant="dot"
        invisible={!hasValueOverride}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ ".MuiBadge-badge": { top: 4 }, flex: 1 }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" width="100%">
          <PropertyField
            projectId={null}
            docId={docId}
            itemId={values.item.id}
            property={newProperty}
            setPropertyValue={async entryValue => {
              await setFieldValue(`[${propertyKey}]`, {
                ...property,
                defaultValue: getEntryValue(entryValue),
              });
              await setFieldTouched(name, true);
            }}
            size="extra-small"
            disableExpressions
            disabled={disabled}
          />
          {fieldError && (
            <Tooltip title={fieldError}>
              <ErrorOutlineIcon fontSize="small" color="error" />
            </Tooltip>
          )}
          <IconButton
            color="primary"
            size={"small"}
            onClick={async () => {
              await setFieldValue(`[${propertyKey}]`, {
                ...property,
                defaultValue: undefined,
              });
              await setFieldTouched(name, true);
            }}
            disabled={disabled || !hasValueOverride}
          >
            <Tooltip title={t("Reset", { ns: "Global" })}>
              <RestartAltIcon />
            </Tooltip>
          </IconButton>{" "}
        </Stack>
      </Badge>
    );
  }

  const unit = getPropertyDisplayedUnit(values.prop, getUnitLabel);
  const displayedValue = getPropDisplayedValue(
    values.prop,
    getBoolLabel,
    getNumberLabel,
    "–",
    unit,
    property?.defaultValue
  );
  return (
    <Badge
      color="secondary"
      variant="dot"
      invisible={!hasValueOverride}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={{ ".MuiBadge-badge": { top: 4 } }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <span>{displayedValue}</span>
        {values.isComputed && (
          <FunctionsIcon fontSize="small" color="secondary" />
        )}
      </Stack>
    </Badge>
  );
}

////////////////////////////////////////////////////////////////////////////////
// Bulk edit action
////////////////////////////////////////////////////////////////////////////////

type BooleanKeys<T> = {
  [K in keyof T]: T[K] extends boolean ? K : never;
}[keyof T];

function checkBulkValue<
  T extends { clientVisibility: boolean },
  K extends BooleanKeys<T>,
>(values: T[], field: K): { isAllTrue: boolean; isAllFalse: boolean } {
  const fieldValues = values.map(value => value[field]);
  const isAllTrue = fieldValues.every(v => v === true);
  const isAllFalse = fieldValues.every(v => v === false);

  return { isAllTrue, isAllFalse };
}

function BulkEditAction({
  selectedProperties,
  docType,
  disabled,
  propsWithItems,
}: {
  selectedProperties: GridRowSelectionModel;
  docType: DocType;
  disabled: boolean;
  propsWithItems: Props2WithItem[];
}) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const viewer = useUserData().currentUser!;

  const formikContext = useFormikContext<FormValues>();
  const selectedValues = React.useMemo(
    () =>
      Object.entries(formikContext.values)
        .filter(([key]) => selectedProperties.ids.has(key))
        .map(([key, value]) => {
          const prop = propsWithItems.find(p => getKey(p) === key);
          assert(prop, `No property with item found for key: ${key}`);
          assert(
            isNonComputedProp(prop),
            `Property with item is computed for key: ${key}`
          );
          return {
            key,
            prompt: "", // synthetic value, not used
            questionControl:
              value.questionControl !== undefined &&
              value.questionControl !== null
                ? value.questionControl
                : prop.questionControl,
            clientVisibility:
              value.clientVisibility !== undefined &&
              value.clientVisibility !== null
                ? value.clientVisibility
                : prop.clientVisibility,
          };
        }),
    [formikContext.values, selectedProperties.ids, propsWithItems]
  );

  const selectedValuesKeys = React.useMemo(
    () => selectedValues.map(v => v.key),
    [selectedValues]
  );

  const clientVisibility = checkBulkValue(selectedValues, "clientVisibility");
  const areAllQuestionControlsSame =
    selectedValues.length > 0 &&
    selectedValues.every(
      v =>
        v.questionControl === selectedValues[0].questionControl ||
        isEqual(v.questionControl, selectedValues[0].questionControl)
    );

  return (
    <>
      <ModalOpenButton
        Modal={ConfirmModal}
        modalProps={{
          title: t("Are you sure you want to reset selected overrides?", {
            ns: "QuoteItem",
          }),
          handleConfirm: async () => {
            await formikContext.setValues({
              ...formikContext.values,
              ...Object.fromEntries(
                selectedValuesKeys.map(key => {
                  const v = formikContext.values[key];
                  return [key, { ...v, ...defaultFormValues }];
                })
              ),
            });
            setTimeout(async () => {
              for (const propertyKey of selectedValuesKeys) {
                await formikContext.setFieldTouched(
                  `[${propertyKey}].defaultValue`
                );
              }
            });
          },
        }}
      >
        <Button
          variant="text"
          color="primary"
          disabled={disabled}
          startIcon={<RestartAltIcon />}
        >
          {t("Reset overrides", { ns: "QuoteItem" })}
        </Button>
      </ModalOpenButton>
      <Divider orientation="vertical" flexItem />
      <Button
        color="primary"
        variant="outlined"
        startIcon={
          <Box height={0} width={32} ml={-1} display="flex" alignItems="center">
            <Checkbox
              size="small"
              checked={clientVisibility.isAllTrue}
              indeterminate={
                !clientVisibility.isAllTrue && !clientVisibility.isAllFalse
              }
              disabled={disabled}
            />
          </Box>
        }
        onClick={async () => {
          const newValue = !clientVisibility.isAllTrue ? true : false;
          await formikContext.setValues({
            ...formikContext.values,
            ...Object.fromEntries(
              selectedValuesKeys.map(key => {
                const v = formikContext.values[key];
                return [key, { ...v, clientVisibility: newValue }];
              })
            ),
          });
        }}
        disabled={disabled}
      >
        {t("Visibility", { ns: "Global" })}
      </Button>
      <ModalOpenButton
        Modal={QuestionControlModal}
        modalProps={{
          hidePrompt: true,
          values:
            selectedValues.length > 0 && areAllQuestionControlsSame
              ? selectedValues[0]
              : defaultQuestionControlValues,
          handleComplete: async values => {
            await formikContext.setValues({
              ...formikContext.values,
              ...Object.fromEntries(
                selectedValuesKeys.map(key => {
                  const v = formikContext.values[key];
                  return [key, { ...v, ...pick(values, "questionControl") }];
                })
              ),
            });
          },
        }}
      >
        <Button color="primary" variant="outlined" disabled={disabled}>
          {t("Question control", { ns: "QuoteItem" })}
        </Button>
      </ModalOpenButton>
    </>
  );
}
